import { Component, Input, OnInit, input } from '@angular/core'
import { FormControl, FormGroup, ReactiveFormsModule, ValidatorFn, Validators } from '@angular/forms'
import {
  BricksDropdownComponent,
  BricksErrorComponent,
  BricksInputComponent,
  BricksOptionComponent,
} from '@enbw_bricks/ui'
import { TooltipComponent } from '../../../shared/components/tooltip/tooltip.component'
import { RequiredAsteriskComponent } from '../../../shared/components/required-asterisk/required-asterisk.component'
import { SolarOrientationOption, SolarResourceFormControls } from '../../../shared/models/technical-resource'
import { DIGITS_NON_LEADING_ZERO_PATTERN } from '../../../shared/validators/validator-pattern'
import { DigitsOnlyDirective } from '../../../shared/directives/digits-only.directive'
import { CustomValidators } from '../../../shared/validators/custom-validators'

@Component({
  selector: 'app-solar-specific-technical-data',
  imports: [
    BricksInputComponent,
    BricksErrorComponent,
    BricksDropdownComponent,
    BricksOptionComponent,
    TooltipComponent,
    RequiredAsteriskComponent,
    ReactiveFormsModule,
    DigitsOnlyDirective,
  ],
  templateUrl: './solar-specific-technical-data.component.html',
  styleUrl: './solar-specific-technical-data.component.scss',
})
export class SolarSpecificTechnicalDataComponent implements OnInit {
  @Input() resource!: FormGroup<SolarResourceFormControls>
  readonly resourceId = input.required<number>()

  protected readonly solarTrackedModulesOptions = [
    {
      value: true, //tracked modules
      label: 'Ja, die PV-Module sind nachgeführt.',
    },
    {
      value: false, //static modules
      label: 'Nein, die PV-Module sind nicht nachgeführt.',
    },
  ]

  protected readonly solarBifacialOptions = [
    { value: true, label: 'Ja, die PV-Module sind bifazial.' },
    {
      value: false,
      label: 'Nein, die PV-Module sind nicht bifazial.',
    },
  ]

  protected readonly solarOrientationOptions: { value: SolarOrientationOption; label: string }[] = [
    { value: 'South', label: 'Süd' },
    { value: 'South-West', label: 'Süd-West' },
    { value: 'South-East', label: 'Süd-Ost' },
    { value: 'West', label: 'West' },
    { value: 'East-West', label: 'Ost-West' },
    { value: 'East', label: 'Ost' },
    { value: 'North-West', label: 'Nord-West' },
    { value: 'North-East', label: 'Nord-Ost' },
    { value: 'North', label: 'Nord' },
    { value: 'North-South', label: 'Nord-Süd' },
  ]

  ngOnInit() {
    // can't use signal because resource comes from @input
    this.resource.controls.solarTrackedModules.valueChanges.subscribe((value) => {
      if (value === false) {
        this.setSolarModuleValidators()
      } else {
        this.clearSolarModuleValidatorsAndValues()
      }
    })
  }

  private setSolarModuleValidators() {
    const currentValue = this.resource.controls.solarTiltDegree.value
    this.resource.setControl(
      'solarTiltDegree',
      new FormControl(currentValue, {
        validators: this.validatorForSolarTiltDegree(),
        updateOn: 'blur',
      })
    )
    this.resource.controls.solarOrientation.setValidators([Validators.required])
    this.resource.controls.solarOrientation.updateValueAndValidity()
  }

  private clearSolarModuleValidatorsAndValues() {
    ;[this.resource.controls.solarTiltDegree, this.resource.controls.solarOrientation].forEach((control) => {
      control.clearValidators()
      control.setValue(null)
      control.updateValueAndValidity()
    })
  }

  private validatorForSolarTiltDegree(): ValidatorFn[] {
    return [
      Validators.required,
      Validators.min(1),
      Validators.max(90),
      Validators.pattern(DIGITS_NON_LEADING_ZERO_PATTERN),
      CustomValidators.zeroIsNotAllowedAsFirstCharacter(),
    ]
  }

  getSolarTiltDegreeError(control: FormControl): string {
    if (control.hasError('zeroIsNotAllowed')) {
      return CustomValidators.zeroIsNotAllowedErrorMessage
    } else if (control.hasError('min') || control.hasError('max')) {
      return 'Die Eingabe muss zwischen 1 und 90 liegen.'
    }
    return ''
  }
}
